import React from 'react'
import { Box, Typography } from '@mui/material'
import { PresentationChartLineIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'

export interface FeedbackEmptyStateProps {
  /**
   * Optional custom title key for translation
   */
  titleKey?: string
  /**
   * Optional custom description key for translation
   */
  descriptionKey?: string
}

/**
 * FeedbackEmptyState - Empty state component for feedback containers
 *
 * Features:
 * - Icon with title and description
 * - Fits inside gradient containers
 * - Centered layout with proper spacing
 * - Consistent with dashboard empty states
 */
export const FeedbackEmptyState: React.FC<FeedbackEmptyStateProps> = ({
  titleKey = 'gamer.feedback.emptyState.title',
  descriptionKey = 'gamer.feedback.emptyState.description'
}) => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        gap: '24px',
        textAlign: 'center',
        width: '100%'
      }}
    >
      {/* Icon */}
      <PresentationChartLineIcon
        style={{
          width: '80px',
          height: '80px',
          color: INTERAX_COLORS.sun
        }}
      />

      {/* Title */}
      <Typography
        sx={{
          ...INTERAX_TYPOGRAPHY.headline4,
          color: INTERAX_COLORS.white
        }}
      >
        {t(titleKey)}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          ...INTERAX_TYPOGRAPHY.body2,
          color: INTERAX_COLORS.thunderGrey,
          maxWidth: '500px',
          lineHeight: '24px'
        }}
      >
        {t(descriptionKey)}
      </Typography>
    </Box>
  )
}

export default FeedbackEmptyState
